import { StaticImage } from 'gatsby-plugin-image';
import type { FC } from 'react';
import { SwiperSlide } from 'swiper/react';

import { AddOnsTemplate } from '@/components/add-ons/template';
import { GalleriesItem } from '@/components/galleries/item';
import { GalleriesList } from '@/components/galleries/list';
import { ComponentsHead } from '@/components/head';
import { AnchorWithTitle } from '@/components/links/anchor';
import { LinkWithTitle } from '@/components/links';

export const HARBOUR_ISLAND_TITLE = 'Harbour Island';

export const Head = () => {
  return (
    <ComponentsHead
      title="Private Boat to Harbour Island, Tampa | Luxury Waterfront Charters"
      description="Dock in Harbour Island by private boat and enjoy upscale dining, skyline views, and relaxing bayside charm in one of Tampa’s most exclusive neighborhoods."
      keywords="Harbour Island, Waterfront Dining, Skyline Views, Luxury Cruise"
    />
  );
};
const Page: FC = () => {
  return (
    <AddOnsTemplate
      title="Harbour Island Boat Charters – Cruise Tampa’s Classiest Waterfront"
      subtitle="Explore Harbour Island with Tampa Bay Boating Adventures"
    >
      <GalleriesList>
        <SwiperSlide>
          <GalleriesItem>
            <StaticImage
              alt={`${HARBOUR_ISLAND_TITLE} header`}
              src="../../images/areas/harbour-island/0.jpg"
            />
          </GalleriesItem>
        </SwiperSlide>
      </GalleriesList>
      <p>
        Located just across the river from downtown Tampa,{' '}
        <AnchorWithTitle href="https://tampamagazines.com/harbour-island-a-tampa-neighborhoods-history/">
          Harbour Island
        </AnchorWithTitle>{' '}
        is one of the city’s most desirable waterfront
        locales. Known for its upscale residences, lush
        greenery, and tranquil vibe, it's the perfect place
        to explore by boat. At Tampa Bay Boating Adventures,
        we offer the chance to cruise around this exclusive
        neighborhood and take in its scenic beauty from a
        unique perspective.
      </p>
      <h3>Available Charters in South Tampa:</h3>
      <p>
        Our charters take you right along the edge of
        Harbour Island, offering incredible views of the
        luxury condos and homes lining the water. As we
        glide past, you can enjoy the beauty of the area,
        the vibrant greenery, and the peaceful waters that
        define Harbour Island. During your cruise, you can
        expect:
      </p>
      <ul>
        <li>
          <LinkWithTitle to="/services/8-hour-private-party-boat-charter/">
            8-Hour Private Party Boat
          </LinkWithTitle>
          : Perfect for a full day of cruising and exploring
          the island’s surroundings, offering plenty of time
          to unwind, socialize, and enjoy the open water.
        </li>
        <li>
          <LinkWithTitle to="/services/6-hour-private-party-boat-charter/">
            6-Hour Private Party Boat
          </LinkWithTitle>
          : A fantastic option for a more relaxed cruise
          with ample time to explore Harbour Island and the
          surrounding areas.
        </li>
        <li>
          <LinkWithTitle to="/services/4-hour-private-party-boat-charter/">
            4-Hour Private Party Boat
          </LinkWithTitle>
          : Ideal for those who want to experience the
          island’s beauty on a more condensed, but still
          enriching, outing.
        </li>
        <li>
          <LinkWithTitle to="/services/sunset-private-party-boat-charter/">
            3-Hour Sunset Charter
          </LinkWithTitle>
          : Wind down with a serene sunset cruise, offering
          beautiful views of Harbour Island’s skyline and
          the Tampa Bay waters at dusk.
        </li>
        <li>
          <LinkWithTitle to="/services/3-hour-wave-rave-djd-nightime-party-boat-charter/">
            3-Hour Wave Rave Charter
          </LinkWithTitle>
          : For those who prefer a more active experience,
          this charter lets you enjoy both the island’s
          views and some fun, water-based excitement.
        </li>
        <li>
          <LinkWithTitle to="/services/private-snorkeling-charters-st-petersburg/">
            Snorkeling Charters
          </LinkWithTitle>
          : If you're looking to take a refreshing dip in
          the water, we can help arrange a snorkeling
          adventure closer to St Petersburg at Egmont Key
          for stunning marine life.
        </li>
        <li>
          <LinkWithTitle to="/services/large-group-charter-boat/">
            Large Group Boat Charter
          </LinkWithTitle>
          : Perfect for larger groups, this service
          accommodates up to 30 passengers and gives you a
          chance to explore the island with friends or
          colleagues in style.
        </li>
      </ul>
      <p>
        Harbour Island offers the best of both worlds –
        quiet, luxurious living alongside the vibrant Tampa
        Bay cityscape. Our charters provide a perfect escape
        to explore the beauty of this peaceful enclave, all
        while enjoying a laid-back day on the water. Book
        your Harbour Island adventure with Tampa Bay Boating
        Adventures today and experience this prestigious
        waterfront destination like never before
      </p>
    </AddOnsTemplate>
  );
};

export default Page;
